import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowDown, LogIn } from "lucide-react";
import HeroCanvas from "@/components/landing/HeroCanvas";
import { scrollToId, portalNotice } from "@/components/landing/Navbar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MaskedLine = ({ children, delay, testid }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      data-testid={testid}
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const [bg, setBg] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND}/api/public/site-images?slot=hero`)
      .then((r) => setBg(r.data[0] || null)).catch(() => {});
  }, []);

  return (
    <section id="top" data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      {bg && (
        <div className="absolute inset-0 z-0" aria-hidden="true" data-testid="hero-bg-image">
          <img src={`${BACKEND}${bg.url}`} alt={bg.alt || ""} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#09090B]/75" />
        </div>
      )}
      <HeroCanvas />
      <div className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(ellipse_at_30%_80%,transparent_20%,rgba(9,9,11,0.7)_100%)]" />

      <div className="pointer-events-none absolute right-6 top-28 z-10 hidden text-right font-tac text-[10px] leading-relaxed tracking-[0.25em] text-white/40 lg:right-16 lg:block">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}>
          N 51°02' // E 13°44'
          <br />
          FUNKTIONSSYSTEM: DOKUMENTATION
          <br />
          BUILD 2026.07
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.9, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute right-4 top-20 z-10 lg:right-16 lg:top-[26%]"
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 3.4 }}
        >
          <img
            data-testid="hero-brand-logo"
            src="/assets/drops-main.png"
            alt="DROPS — Drohnen Service und Technik"
            className="w-28 object-contain drop-shadow-[0_0_30px_rgba(245,179,1,0.15)] sm:w-40 lg:w-72"
          />
        </motion.div>
      </motion.div>

      <div className="relative z-10 flex flex-1 flex-col justify-end px-6 pb-16 pt-32 lg:px-16 lg:pb-20">
        <motion.div
          data-testid="hero-status-badge"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mb-8 flex w-fit items-center gap-3 border border-white/15 bg-black/40 px-4 py-2 backdrop-blur-md"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping bg-[#F5B301] opacity-75" />
            <span className="relative inline-flex h-2 w-2 bg-[#F5B301]" />
          </span>
          <span className="font-tac text-[10px] tracking-[0.3em] text-white/80">
            DROPS TFR — TECHNICAL FIRST RESPONDER
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-[11vw] font-black uppercase leading-[0.85] tracking-tighter lg:text-[9.5vw]"
        >
          <span className="sr-only">Technische Spezialinspektionen mit Drohnentechnik</span>
          <span aria-hidden="true">
            <MaskedLine delay={0.35} testid="hero-line-1">
              Technische
            </MaskedLine>
            <MaskedLine delay={0.47} testid="hero-line-2">
              Spezial-
            </MaskedLine>
            <MaskedLine delay={0.59} testid="hero-line-3">
              <span className="text-[#F5B301]">Inspektionen.</span>
            </MaskedLine>
          </span>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-2xl font-display text-lg font-bold uppercase tracking-tight text-white sm:text-xl"
        >
          Bauwerke digital erfassen. Schäden sichtbar machen. Entscheidungen vorbereiten.
        </motion.p>

        <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-md font-tac text-sm leading-relaxed text-[#A1A1AA]"
          >
            DROPS TFR unterstützt <strong className="font-semibold text-white">Ingenieurbüros, Bauunternehmen, Betreiber und öffentliche Auftraggeber</strong> bei der technischen Erfassung und Dokumentation schwer zugänglicher Bauwerke und Anlagen. Mit moderner Drohnentechnik erfassen wir hochauflösende Bilddaten, georeferenzierte Aufnahmen, 360°-Ansichten und photogrammetrische 3D-Daten — bereitgestellt für die weitere fachliche Bearbeitung.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("#leistungen")}
              className="group relative overflow-hidden bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
            >
              <span className="absolute inset-0 -translate-x-full bg-white/20 transition-transform duration-300 group-hover:translate-x-0" />
              <span className="relative z-10">LEISTUNGEN ENTDECKEN</span>
            </button>
            <button
              type="button"
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("#kontakt")}
              className="group flex items-center gap-2 border border-white/25 px-8 py-4 font-tac text-xs tracking-[0.25em] text-white/90 transition-colors duration-300 hover:border-white hover:text-white"
            >
              KONTAKT AUFNEHMEN
            </button>
            <button
              type="button"
              data-testid="hero-cta-login"
              onClick={portalNotice}
              className="group flex items-center gap-2 px-2 py-4 font-tac text-xs tracking-[0.25em] text-white/60 transition-colors duration-300 hover:text-white"
            >
              <LogIn className="h-4 w-4" />
              KUNDEN-LOGIN
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="scroll-indicator"
        onClick={() => scrollToId("#funktionen")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
        aria-label="Nach unten scrollen"
      >
        <span className="font-tac text-[9px] tracking-[0.4em] text-white/40">SCROLL</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="h-4 w-4 text-[#F5B301]" />
        </motion.span>
      </motion.button>
    </section>
  );
}
